//
// ABSOLUTE ROUTES FOR COMPONENTS
//

import { SCHEDULE_SESSION_DISPLAY_TYPE_ROUND } from 'common-data-handling';
import { useMemo } from 'react';
import addURLParameters from '../../misc/addURLParameters';
import { DATE_FORMAT_URL } from '../constants/datetime';
import { eventMomentFactory } from '../misc/eventMomentFactory';
import { useEvent } from './eventHooks';
import { useMeeting } from './meeting/useMeeting';
import { useProgramSession } from './schedule/useProgramSession';

/**
 * Returns a formatted URL for scheduling a session based on the event and program ID.
 *
 * @param {Number} eventId - The ID of the event
 * @param {Number} programId - The ID of the program
 * @return {string} The formatted URL with parameters for scheduling the session
 */
export function useScheduleSessionRoute(eventId, programId) {
    const event = useEvent(eventId),
        item = useProgramSession(eventId, programId);

    return useMemo(() => {
        const path = ['/events', eventId],
            params = {};
        if (item) {
            path.push('schedule', eventMomentFactory(event, item.start_local).format(DATE_FORMAT_URL));
            if (item.display_type == SCHEDULE_SESSION_DISPLAY_TYPE_ROUND) {
                params.round = item.id;
            } else {
                path.push(item.id);
            }
        }
        return addURLParameters(path.join('/'), params);
    }, [item]);
}

/**
 * Returns a formatted URL for a meeting based on the event and meeting IDs.
 *
 * @param {number} eventId - The ID of the event.
 * @param {number} meetingId - The ID of the meeting.
 * @return {string} The formatted URL with parameters for the meeting.
 */
export function useMeetingRoute(eventId, meetingId) {
    const event = useEvent(eventId),
        meeting = useMeeting(eventId, meetingId);

    return useMemo(() => {
        const path = ['/events', eventId];

        if (meeting) {
            path.push('meetings', eventMomentFactory(event, meeting.start_local).format(DATE_FORMAT_URL), meetingId);
        }

        return path.join('/');
    }, [meeting]);
}
